import { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { Theme, css } from '@emotion/react';
import { Folder, RocketLaunch } from '@phosphor-icons/react';
import type { ProjectListProject } from 'api/project/routes/projectListRoute';
import { DeploymentState } from 'common/CommonConstants';
import { createVSCodeUri, getPlaceholderUrl } from 'common/commonUtil';
import { useDeploymentStatusTreesState } from 'client/state/useDeploymentStatusTree';
import { deploymentSortComparator } from 'client/pages/deployment/deploymentUtils';
import { getDeploymentStageIcon } from 'client/ClientConstants';
import { Button } from 'client/components/Button';
import { ListItemFigure } from 'client/components/Other/ListItemFigure';
import { ListItemText } from 'client/components/Other/ListItemText';
import { getFeaturesFromState } from 'client/components/Status/statusUtils';
import { Typography } from 'client/components/Typography';
import vscodeLogo from 'client/static/image/vscode-alt.svg';
import { Transition, applyEffect, borderRadius } from 'client/Theme';

export const ProjectListItem = ({ id: projectID, name, deployments, pins, isQuickLaunch }: ProjectListProject) => {
  const { statusTrees } = useDeploymentStatusTreesState();

  const image = { alt: 'project image', src: getPlaceholderUrl('project', projectID), shape: 'hexagon' as const };

  const items = [...deployments]
    .sort((a, b) => deploymentSortComparator(a, b, pins))
    .filter((item) => {
      const statusTree = statusTrees.find((state) => state.deploymentID === item.id)?.statusTree;
      return statusTree?.deploymentStateID != DeploymentState.DELETED;
    });

  const emptyItems = new Array(Math.max(0, 3 - items.length)).fill(null);
  const overflowItemCount = Math.max(0, items.length - 3);

  const handleVSCodeClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
  };

  return (
    <Link to={`../project/${projectID}`} css={itemStyle}>
      <header css={headerStyle}>
        <ListItemFigure
          image={image}
          ornament={isQuickLaunch ? <RocketLaunch style={{ fill: 'url(#gradient-one)' }} /> : undefined}
          size={40}
          css={figureStyle}
        />
        <ListItemText
          primary={name}
          secondary={
            <>
              <Folder /> Project · {items.length} deployment(s)
            </>
          }
          primaryProps={{ variant: 'h5', noWrap: true }}
          secondaryProps={{ component: 'span' }}
          css={titleStyle}
        />
      </header>
      <ol css={listStyle}>
        {items.slice(0, 3).map((deployment, index) => {
          const { id: deploymentID, name, stage, hosts, coreDomainName } = deployment;
          const statusTree = statusTrees.find((state) => state.deploymentID === deploymentID)?.statusTree;
          const host = hosts[0];
          const url = host?.customDomain || host?.tweakedDomain || host?.defaultDomain || '';
          // const ssh = getSshUsers(templateConfiguration.ssh, stage || '').shift();
          // const sshUsersAndEditorPaths = getSshUsers(templateConfiguration.ssh, stage || '');
          // const sshUrl = `${sshUsersAndEditorPaths.length ? ssh?.username : '--'}@${coreDomainName}`;
          // const sshEditorPath = sshUsersAndEditorPaths.length ? ssh?.editorPath || '/app' : '/app';
          const isPinned = pins.includes(deploymentID);

          const state = {
            deploymentStateID: statusTree?.deploymentStateID,
            coreStateID: statusTree?.core.coreStateID,
            itemStateID: statusTree?.itemStateID,
          };

          const stateFeatures = getFeaturesFromState(state);

          return (
            <li key={index} css={[isPinned && pinnedStyle]}>
              <Link to={`../project/${projectID}/deployment/${deploymentID}`}>
                <ListItemFigure size={24} icon={getDeploymentStageIcon(stage)} state={state} />
                <ListItemText
                  primary={name}
                  secondary={url}
                  primaryProps={{ noWrap: true }}
                  secondaryProps={{ component: 'span', noWrap: true }}
                />
              </Link>
              {/*stage === 'development' && (
                <a href={createVSCodeUri(sshUrl, sshEditorPath)} onClick={handleVSCodeClick}>
                  <Button disabled={!stateFeatures.isEnabled}>
                    <img src={vscodeLogo} width={22} height={22} />
                  </Button>
                </a>
              )*/}
            </li>
          );
        })}

        {emptyItems.map((_, index) => (
          <li key={index} css={emptyStyle} />
        ))}

        {!!overflowItemCount && (
          <Typography variant="caption" css={moreIndicatorStyle}>
            <span>+{overflowItemCount} more</span>
          </Typography>
        )}
      </ol>
    </Link>
  );
};

const itemStyle = (theme: Theme) => css`
  padding: 24px;
  max-width: 100%;
  user-select: none;
  text-decoration: none;
  background-color: ${theme.color.grey[60]};
  border-radius: ${borderRadius}px;

  transition: background-color ${Transition.basic};

  &:hover {
    background-color: ${applyEffect(theme.color.grey[60], 'hover')};
  }
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const titleStyle = css`
  > span {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: -4px;
  }
`;

const figureStyle = css`
  margin-right: 0.25rem;

  .ornament > svg {
    width: 24px;
    height: 24px;
  }
`;

const listStyle = (theme: Theme) => css`
  position: relative;
  margin: 0;
  margin-top: 20px;
  padding: 0;
  padding-left: 51px;
  width: 100%;
  list-style: none;

  > li {
    position: relative;
    display: flex;
    gap: 2px;
    margin-bottom: 8px;
    height: 50px;
    width: 100%;
    min-height: auto;

    > a:first-of-type {
      display: flex;
      align-items: center;
      flex: 1;
      padding: 0 8px;
      text-decoration: none;
      background-color: rgba(0, 0, 0, 0.05);
      border-radius: ${borderRadius}px 0 0 ${borderRadius}px;

      transition: background-color ${Transition.basic};

      &:hover {
        background-color: ${applyEffect(theme.color.primary[100], 'hover')};
      }

      &:last-of-type {
        border-radius: ${borderRadius}px;
      }

      > figure {
        width: 24px !important;
        height: 24px !important;
        margin-right: 12px;

        .ornament {
          transform: scale(0.75);
        }
      }

      p {
        margin: 0;
      }

      span {
        display: block;
        margin-top: -4px;
      }
    }

    > a > button {
      width: 50px;
      height: 50px;
      background-color: rgba(0, 0, 0, 0.05);
      border-radius: 0 ${borderRadius}px ${borderRadius}px 0;

      transition: background-color ${Transition.basic};

      &:hover:not(:disabled) {
        background-color: ${applyEffect(theme.color.primary[100], 'hover')};
      }

      &:disabled {
        border: 2px solid rgba(0, 0, 0, 0.05);
        background-color: transparent;
      }

      > span {
        display: flex;
        margin: 0;
      }
    }
  }
`;

const emptyStyle = css`
  li& {
    background-color: transparent;
    border: 2px solid rgba(0, 0, 0, 0.05);
    border-radius: ${borderRadius}px;
  }
`;

const moreIndicatorStyle = css`
  position: absolute;
  display: flex;
  justify-content: center;
  width: calc(100% - 51px);

  > span {
    margin-top: 3px;
    padding: 0px 4px;
    border-radius: 64px;
    z-index: 2;
    opacity: 0.5;
  }

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 50px;
    background-color: rgba(0, 0, 0, 0.05);
    border: 2px solid transparent;
    border-radius: ${borderRadius}px;
    z-index: 1;
  }
`;

const pinIcon = (color: string) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 256 256'%3E%3Cpath fill='${color.replace(
    '#',
    '%23'
  )}' d='m235 104-53 54c4 12 6 34-13 60a16 16 0 0 1-12 6h-1a16 16 0 0 1-11-5l-49-48-42 43a8 8 0 0 1-12-12l43-42-48-49a16 16 0 0 1 1-23c25-21 50-17 60-14l54-53a16 16 0 0 1 23 0l60 60a16 16 0 0 1 0 23Z'/%3E%3C/svg%3E")`;

const pinnedStyle = (theme: Theme) => css`
  overflow: visible;

  &::after {
    content: '';
    position: absolute;
    display: block;
    top: -6px;
    left: -6px;
    width: 16px;
    height: 16px;
    background-color: ${theme.color.primary[100]};
    background-image: ${pinIcon(theme.color.white)};
    background-size: 10px;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 32px;
  }
`;
